import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Observable, defer, finalize, take, timeout } from 'rxjs';
import { AdminSectionBaseComponent } from 'src/app/shared/admin-section-base';
import {
  AdminApiService,
  Demo,
  DemoFile,
  GitHubFile,
  GitHubPR,
} from 'src/app/shared/services/admin-api.service';

@Component({
  standalone: false,
  selector: 'app-pending-files',
  templateUrl: './pending-files.component.html',
  styleUrls: ['./pending-files.component.scss'],
})
export class PendingFilesComponent extends AdminSectionBaseComponent implements OnInit {
  activeModal: 'quick-add' | null = null;

  demos: Demo[] = [];
  demoFiles: DemoFile[] = [];
  githubFiles: GitHubFile[] = [];
  pullRequests: GitHubPR[] = [];
  selectedPrNumber: number | null = null;
  prFiles: GitHubFile[] = [];
  pendingFiles: GitHubFile[] = [];
  notebookViewMode: 'all' | 'pending' = 'pending';
  pendingFilesSearchTerm = '';

  githubLoading = false;
  private githubRequestCount = 0;

  // Quick-add state
  quickAddFile: GitHubFile | null = null;
  quickAddMode: 'existing' | 'new' = 'existing';
  quickAddDemoSearch = '';
  quickAddSelectedDemoId: number | null = null;
  quickAddNewDemoTitle = '';
  quickAddNewDemoType = '';
  quickAddNewDemoAvailable: 'Y' | 'N' | 'P' = 'P';
  quickAddNewDemoShortDescription = '';
  quickAddLabel = '';
  quickAddFileType: 'notebook' | 'nbviewer' = 'notebook';

  constructor(
    private adminApiService: AdminApiService,
    snackBar: MatSnackBar,
    cdr: ChangeDetectorRef,
  ) {
    super(snackBar, cdr);
    this.initPagination('pendingFiles', 20);
  }

  ngOnInit(): void {
    this.refreshAll();
  }

  refreshAll(): void {
    this.trackApiCall(this.adminApiService.listDemos(this.token)).subscribe({
      next: (items) => { this.demos = items; },
      error: (error) => this.handleError(error),
    });
    this.trackApiCall(this.adminApiService.listDemoFiles(this.token)).subscribe({
      next: (items) => {
        this.demoFiles = items;
        this.updatePendingFiles();
      },
      error: (error) => this.handleError(error),
    });
  }

  updatePendingFiles(): void {
    this.trackGitHubCall(this.adminApiService.fetchGitHubJupyterDemosFiles()).subscribe({
      next: (files) => {
        this.githubFiles = files;
        this.pendingFiles = this.adminApiService.getPendingGitHubFiles(files, this.demoFiles);
      },
      error: (error) => console.error('Failed to fetch GitHub files:', error),
    });

    this.trackGitHubCall(this.adminApiService.fetchGitHubPullRequests()).subscribe({
      next: (prs) => { this.pullRequests = prs; },
      error: (error) => console.error('Failed to fetch GitHub PRs:', error),
    });
  }

  getDisplayedNotebooks(): GitHubFile[] {
    if (this.selectedPrNumber !== null) {
      return this.filterNotebooksBySearch(this.prFiles);
    }
    const notebooks = this.notebookViewMode === 'pending' ? this.pendingFiles : this.githubFiles;
    return this.filterNotebooksBySearch(notebooks);
  }

  filterNotebooksBySearch(notebooks: GitHubFile[]): GitHubFile[] {
    if (!this.pendingFilesSearchTerm.trim()) {
      return notebooks;
    }
    const searchTerm = this.pendingFilesSearchTerm.toLowerCase();
    return notebooks.filter(file =>
      file.name.toLowerCase().includes(searchTerm) ||
      file.path.toLowerCase().includes(searchTerm)
    );
  }

  setNotebookViewMode(mode: 'all' | 'pending'): void {
    this.notebookViewMode = mode;
    this.selectedPrNumber = null;
    this.prFiles = [];
    this.resetPage('pendingFiles');
  }

  onPrSelected(prNumber: number | null): void {
    this.selectedPrNumber = prNumber;
    this.prFiles = [];
    this.resetPage('pendingFiles');
    if (prNumber === null) { return; }
    this.trackApiCall(this.adminApiService.fetchGitHubPRFiles(prNumber)).subscribe({
      next: (files) => { this.prFiles = files; },
      error: (error) => console.error('Failed to fetch PR files:', error),
    });
  }

  closeModal(): void {
    this.activeModal = null;
  }

  openQuickAdd(file: GitHubFile): void {
    this.quickAddFile = file;
    this.quickAddMode = 'existing';
    this.quickAddDemoSearch = '';
    this.quickAddSelectedDemoId = null;
    this.quickAddNewDemoTitle = '';
    this.quickAddNewDemoType = '';
    this.quickAddNewDemoAvailable = 'P';
    this.quickAddNewDemoShortDescription = '';
    this.quickAddLabel = '';
    this.quickAddFileType = 'notebook';
    this.activeModal = 'quick-add';
  }

  filteredDemosForQuickAdd(): Demo[] {
    const q = typeof this.quickAddDemoSearch === 'string'
      ? this.quickAddDemoSearch.toLowerCase()
      : (this.quickAddDemoSearch as any)?.demo_title?.toLowerCase() ?? '';
    return this.demos.filter(d => d.demo_title.toLowerCase().includes(q)).slice(0, 15);
  }

  displayDemoTitle = (demo: Demo | string): string => {
    if (!demo) { return ''; }
    return typeof demo === 'string' ? demo : demo.demo_title;
  };

  selectDemoForQuickAdd(demo: Demo): void {
    this.quickAddSelectedDemoId = demo.id;
    this.quickAddDemoSearch = demo.demo_title;
  }

  saveQuickAdd(): void {
    if (!this.quickAddFile || !this.quickAddLabel.trim()) {
      this.showError('Label is required.');
      return;
    }

    const file = this.quickAddFile;
    const directory = file.path.substring(0, file.path.lastIndexOf('/'));

    const createFileForDemo = (demoId: number) => {
      const actionKey = this.quickAddActionKey();
      const payload: Partial<DemoFile> = {
        id: demoId,
        label: this.quickAddLabel.trim(),
        directory,
        filename: file.name,
        demo_type: this.quickAddFileType,
      };
      this.trackActionCall(actionKey, this.adminApiService.createDemoFile(this.token, payload)).subscribe({
        next: () => {
          this.showSuccess(`File added to demo successfully.`);
          this.activeModal = null;
          this.quickAddFile = null;
          this.trackActionCall(actionKey, this.adminApiService.listDemoFiles(this.token)).subscribe((items) => {
            this.demoFiles = items;
            this.updatePendingFiles();
          });
        },
        error: (error) => this.handleError(error),
      });
    };

    if (this.quickAddMode === 'existing') {
      if (!this.quickAddSelectedDemoId) {
        this.showError('Please select a demo.');
        return;
      }
      createFileForDemo(this.quickAddSelectedDemoId);
    } else {
      if (!this.quickAddNewDemoTitle.trim() || !this.quickAddNewDemoType.trim() || !this.quickAddNewDemoAvailable) {
        this.showError('Demo title, type and availability are required.');
        return;
      }
      const demoPayload: Partial<Demo> = {
        demo_title: this.quickAddNewDemoTitle.trim(),
        demo_type: this.quickAddNewDemoType.trim(),
        available: this.quickAddNewDemoAvailable,
        short_description: this.toNullableString(this.quickAddNewDemoShortDescription),
      };
      const actionKey = this.quickAddActionKey();
      this.trackActionCall(actionKey, this.adminApiService.createDemo(this.token, demoPayload)).subscribe({
        next: (created: Demo) => {
          this.trackActionCall(actionKey, this.adminApiService.listDemos(this.token)).subscribe((items) => {
            this.demos = items;
            createFileForDemo(created.id);
          });
        },
        error: (error) => this.handleError(error),
      });
    }
  }

  refreshActionKey(): string {
    return 'refresh-pending-files';
  }

  quickAddActionKey(): string {
    return `quick-add:${this.quickAddFile?.path || 'none'}`;
  }

  private trackGitHubCall<T>(request$: Observable<T>): Observable<T> {
    return defer(() => {
      this.githubRequestCount += 1;
      this.githubLoading = true;
      return request$.pipe(take(1), timeout(this.apiCallTimeoutMs));
    }).pipe(
      finalize(() => this.applyState(() => {
        this.githubRequestCount = Math.max(0, this.githubRequestCount - 1);
        this.githubLoading = this.githubRequestCount > 0;
      })),
    );
  }
}
